import { toDate } from "date-fns";
import type React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

import DateRangeSelector from "../../../components/DateRangeSelector";

const MedicalDashboardChart = ({
  data,
  dateRange,
  fromDate,
  toDate,
  onDateRangeChange,
  onFromDateChange,
  onToDateChange,
}: {
  data: any[];
  dateRange: string;
  fromDate: string;
  toDate: string;
  onDateRangeChange: (value: string) => void;
  onFromDateChange: (date: string) => void;
  onToDateChange: (date: string) => void;
}) => {
  const maxCount = Math.max(...data.map(d => d.count), 30);

  // Truncate long test names for display
  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Process data to add truncated names
  const processedData = data.map(item => ({
    ...item,
    displayName: truncateText(item.testName),
    fullName: item.testName,
  }));

  // Custom tooltip to show full test name
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded shadow-lg">
          <p className="text-sm font-semibold text-gray-700">
            {payload[0].payload.fullName}
          </p>
          <p className="text-sm text-gray-600">
            Count:{" "}
            <span className="font-bold text-sky-500">{payload[0].value}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom Y-axis tick with full name on hover
  const CustomYAxisTick = ({ x, y, payload }: any) => {
    const item = processedData.find(d => d.displayName === payload.value);
    return (
      <g transform={`translate(${x},${y})`}>
        <title>{item?.fullName || payload.value}</title>
        <text
          x={0}
          y={0}
          dy={4}
          textAnchor="end"
          fill="#6b7280"
          fontSize={12}
          style={{ cursor: "pointer" }}
        >
          {payload.value}
        </text>
      </g>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold mb-4 text-gray-700">
          Top tests performed{" "}
          {dateRange === "custom"
            ? `between ${fromDate} and ${toDate}`
            : `${dateRange}`}
        </h3>
        <DateRangeSelector
          preset="lab"
          compact
          dateRange={dateRange}
          fromDate={fromDate}
          toDate={toDate}
          onDateRangeChange={onDateRangeChange}
          onFromDateChange={onFromDateChange}
          onToDateChange={onToDateChange}
        />
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={processedData}
          layout="vertical"
          margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid
            strokeDasharray="1 1"
            horizontal={false}
            stroke="#e5e7eb"
          />
          <XAxis
            type="number"
            domain={[0, maxCount]}
            stroke="#9ca3af"
            fontSize={12}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="displayName"
            width={300}
            tickLine={false}
            axisLine={false}
            fontSize={12}
            stroke="#6b7280"
            interval={0}
            tick={<CustomYAxisTick />}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="count" fill="#0ea5e9" radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MedicalDashboardChart;
